import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { helpRoutes } from '../../routes';
import {
    useGetAllArticles,
    useGetAllArticlesByCategory,
    useGetCategoryById,
} from '../../sic-lib/api/hooks/useHelp';
import { HELP_ARTICLE_SORT } from '../../sic-lib/defaults';
import { Article, ArticleSort } from '../../sic-lib/types/Article';
import Loader from '../Shared/Loader';

export default function HelpViewCategory() {
    const { categoryId } = useParams<{ categoryId: string }>();
    const history = useHistory();

    const [sort, setSort] = React.useState<ArticleSort>(HELP_ARTICLE_SORT[0]);

    const { data: categoryData, isLoading: categoryLoading } = useGetCategoryById(+categoryId);

    const { data: articleByCategoryId, isLoading: articleByCategoryLoading } =
        useGetAllArticlesByCategory(+categoryId);

    const { data: articlesData, isLoading: articlesLoading } = useGetAllArticles();

    const categoryName = React.useMemo(() => {
        return categoryData?.categoryName || '';
    }, [categoryData]);

    const articlesByCategory = React.useMemo(() => {
        if (articleByCategoryId) {
            return sortArticles(articleByCategoryId, sort).filter(category => !category.isSticky);
        }
        return [];
        // return articleByCategoryId?.filter(category => !category.isSticky) || [];
    }, [articleByCategoryId, sort]);

    const importantArticles = React.useMemo(() => {
        return articlesData?.filter(article => article.isSticky) || [];
    }, [articlesData]);

    const handleClick = (articleId: number) => {
        history.push(helpRoutes.setArticle(articleId));
    };

    const handleSortChange = (sortType: ArticleSort) => {
        if (sortType) {
            setSort(sortType);
        }
    };

    return (
        <>
            <Loader isLoading={categoryLoading || articleByCategoryLoading}>
                <Card>
                    <CardHeader
                        title={categoryName}
                        action={
                            <div>
                                <TextField
                                    id="outlined-select-sort"
                                    label="Sort"
                                    select
                                    defaultValue={sort.value}
                                >
                                    {HELP_ARTICLE_SORT.map(option => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            onClick={() => handleSortChange(option)}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        }
                    />
                    <Divider />
                    <CardContent>
                        <Stack
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                        >
                            {articlesByCategory.length > 0 ? (
                                articlesByCategory.map(article => {
                                    return (
                                        <Button
                                            key={article.articleId}
                                            color="primary"
                                            onClick={() => handleClick(article.articleId)}
                                        >
                                            {article.title}
                                        </Button>
                                    );
                                })
                            ) : (
                                <Alert severity="info">No articles found for this category.</Alert>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </Loader>
            <Loader isLoading={articlesLoading}>
                <Card>
                    <CardHeader title="Important Articles" />
                    <Divider />
                    <CardContent>
                        <Stack
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                        >
                            {importantArticles.map(article => {
                                return (
                                    <Button
                                        key={article.articleId}
                                        color="primary"
                                        sx={{ marginBottom: 2 }}
                                        onClick={() => handleClick(article.articleId)}
                                    >
                                        {article.title}
                                    </Button>
                                );
                            })}
                        </Stack>
                    </CardContent>
                </Card>
            </Loader>
        </>
    );
}

const sortArticles = (articles: Article[], sort: ArticleSort) => {
    let sortedArticles: Article[] = [];

    switch (sort.value) {
        case 'OrderNum':
            sortedArticles = articles.sort((a, b) => a.orderNum - b.orderNum);
            break;
        case 'ViewCount':
            sortedArticles = articles.sort((a, b) => b.viewCount - a.viewCount);
            break;
        case 'DateCreatedNew':
            sortedArticles = articles.sort(
                (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
            );
            break;
        case 'DateCreatedOld':
            sortedArticles = articles.sort(
                (a, b) => new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime(),
            );
            break;
        case 'TitleA-Z':
            sortedArticles = articles.sort((a, b) => a.title.localeCompare(b.title));
            break;
        case 'TitleZ-A':
            sortedArticles = articles.sort((a, b) => b.title.localeCompare(a.title));
            break;
        default:
            sortedArticles = articles;
            break;
    }

    return sortedArticles;
};
